import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  Plus, 
  CheckCircle, 
  AlertTriangle,
  Loader2,
  Truck,
  Package,
  Download,
  Mail,
  Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { getHireOrder, updateHireOrder, addItemToOrder, getAssets, downloadDeliveryNote, downloadCollectionNote, emailNoteToClient } from '../lib/api';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const styles = {
    confirmed: 'badge-info',
    dispatched: 'badge-warning',
    on_hire: 'badge-success',
    completed: 'badge-neutral',
    overdue: 'badge-error',
    pending: 'badge-neutral',
    delivered: 'badge-success',
    collected: 'badge-info',
  };
  return (
    <span className={`${styles[status] || 'badge-neutral'} inline-block`}>
      {status.replace('_', ' ')}
    </span>
  );
};

export default function HireOrderDetailPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [addItemDialog, setAddItemDialog] = useState(false);
  const [emailDialog, setEmailDialog] = useState({ open: false, type: 'delivery' });
  const [emailForm, setEmailForm] = useState({ recipient_email: '', recipient_name: '', custom_message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchOrder = async () => {
    try {
      const response = await getHireOrder(id);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableAssets = async () => {
    try {
      const response = await getAssets({ status: 'available' });
      setAvailableAssets(response.data);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  useEffect(() => {
    fetchOrder();
    fetchAvailableAssets();
  }, [id]);

  const handleAddItem = async (assetId) => {
    try {
      await addItemToOrder(order.order_number, assetId);
      toast.success('Item added to order');
      setAddItemDialog(false);
      fetchOrder();
      fetchAvailableAssets();
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await updateHireOrder(order.order_number, { status: newStatus });
      toast.success(`Order marked as ${newStatus}`);
      fetchOrder();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.recipient_email) {
      toast.error('Please enter recipient email');
      return;
    }
    
    setSendingEmail(true);
    try {
      await emailNoteToClient(order.order_number, {
        recipient_email: emailForm.recipient_email,
        recipient_name: emailForm.recipient_name,
        note_type: emailDialog.type,
        custom_message: emailForm.custom_message
      });
      toast.success(`${emailDialog.type === 'delivery' ? 'Delivery' : 'Collection'} note sent successfully!`);
      setEmailDialog({ open: false, type: 'delivery' });
      setEmailForm({ recipient_email: '', recipient_name: '', custom_message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const openEmailDialog = (type) => {
    setEmailForm({ 
      recipient_email: '', 
      recipient_name: order?.client_name || '', 
      custom_message: '' 
    });
    setEmailDialog({ open: true, type });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h2 className="font-heading font-bold text-xl">Order Not Found</h2>
        <Link to="/hire-orders" className="text-primary hover:underline mt-2 inline-block">
          Back to Hire Orders
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hire-order-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/hire-orders">
          <Button variant="outline" size="icon" className="border-2 border-slate-200">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-heading font-black text-3xl text-slate-900 uppercase tracking-tight">
              {order.order_number}
            </h1>
            <StatusBadge status={order.status} />
          </div>
          <p className="font-body text-slate-500 mt-1">{order.client_name}</p>
        </div>
      </div>

      {/* Order Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-2 border-slate-200 shadow-industrial">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading font-bold uppercase text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              Hire Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="font-mono text-xs uppercase text-slate-400 mb-1">Client</p>
                <p className="font-body font-semibold">{order.client_name}</p>
              </div>
              <div>
                <p className="font-mono text-xs uppercase text-slate-400 mb-1">Site Address</p>
                <p className="font-body font-semibold">{order.site_address}</p>
              </div>
              <div>
                <p className="font-mono text-xs uppercase text-slate-400 mb-1">Start Date</p>
                <p className="font-body font-semibold">{order.start_date}</p>
              </div>
              <div>
                <p className="font-mono text-xs uppercase text-slate-400 mb-1">End Date</p>
                <p className="font-body font-semibold">{order.end_date}</p>
              </div>
              <div>
                <p className="font-mono text-xs uppercase text-slate-400 mb-1">Total Value</p>
                <p className="font-heading font-bold text-2xl text-primary">£{order.total_value?.toFixed(2)}</p>
              </div>
              <div>
                <p className="font-mono text-xs uppercase text-slate-400 mb-1">Items</p>
                <p className="font-heading font-bold text-2xl">{order.items?.length || 0}</p>
              </div>
              
              {/* Calculated Hire Totals - shown after collection */}
              {order.calculated_total > 0 && (
                <>
                  <div className="col-span-2 border-t-2 border-slate-200 pt-4 mt-2">
                    <p className="font-mono text-xs uppercase text-slate-400 mb-2">Calculated Hire Charge</p>
                    <div className="bg-green-50 border-2 border-green-200 rounded p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-body text-slate-600">Actual Days on Hire:</span>
                        <span className="font-heading font-bold">{order.actual_hire_days || 0} days</span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-body text-slate-600">Weeks Charged (min 1):</span>
                        <span className="font-heading font-bold">{order.hire_weeks || 0} week(s)</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-green-200">
                        <span className="font-heading font-bold uppercase">Calculated Total:</span>
                        <span className="font-heading font-black text-2xl text-green-600">£{order.calculated_total?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <Card className="border-2 border-slate-200 shadow-industrial">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading font-bold uppercase text-sm">
              Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-2">
            <Dialog open={addItemDialog} onOpenChange={setAddItemDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full justify-start border-2 border-slate-200">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md border-2 border-slate-200 shadow-industrial">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold uppercase">
                    Add Item to Order
                  </DialogTitle>
                </DialogHeader>
                <div className="max-h-64 overflow-y-auto">
                  {availableAssets.length === 0 ? (
                    <p className="text-center text-slate-500 py-4">No available assets</p>
                  ) : (
                    availableAssets.map((asset) => (
                      <button
                        key={asset.id}
                        onClick={() => handleAddItem(asset.asset_id)}
                        className="w-full flex items-center gap-3 p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 text-left"
                      >
                        <Package className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="font-body font-semibold">{asset.name}</p>
                          <p className="font-mono text-xs text-slate-500">{asset.asset_id}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Delivery Note Actions */}
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => downloadDeliveryNote(order.order_number)}
                className="flex-1 justify-center border-2 border-slate-200"
                data-testid="download-delivery-note-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                PDF
              </Button>
              <Button 
                variant="outline"
                onClick={() => openEmailDialog('delivery')}
                className="flex-1 justify-center border-2 border-slate-200 text-primary hover:bg-primary/5"
                data-testid="email-delivery-note-btn"
              >
                <Mail className="w-4 h-4 mr-1" />
                Email
              </Button>
            </div>
            <p className="text-xs text-slate-400 -mt-1 mb-2">Delivery Note</p>

            {/* Collection Note Actions */}
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => downloadCollectionNote(order.order_number)}
                className="flex-1 justify-center border-2 border-slate-200"
                data-testid="download-collection-note-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                PDF
              </Button>
              <Button 
                variant="outline"
                onClick={() => openEmailDialog('collection')}
                className="flex-1 justify-center border-2 border-slate-200 text-primary hover:bg-primary/5"
                data-testid="email-collection-note-btn"
              >
                <Mail className="w-4 h-4 mr-1" />
                Email
              </Button>
            </div>
            <p className="text-xs text-slate-400 -mt-1 mb-2">Collection Note</p>

            {order.status === 'confirmed' && (
              <Button 
                onClick={() => handleUpdateStatus('dispatched')}
                className="w-full justify-start bg-primary hover:bg-primary-hover"
              >
                <Truck className="w-4 h-4 mr-2" />
                Mark as Dispatched
              </Button>
            )}
            
            {order.status === 'on_hire' && (
              <Button 
                onClick={() => handleUpdateStatus('completed')}
                className="w-full justify-start bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark as Complete
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="items" className="w-full">
        <TabsList className="bg-white border-2 border-slate-200 shadow-industrial">
          <TabsTrigger value="items" className="font-heading uppercase text-sm">
            Items on Hire
          </TabsTrigger>
          <TabsTrigger value="notes" className="font-heading uppercase text-sm">
            Notes & Attachments
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="items">
          <Card className="border-2 border-slate-200 shadow-industrial">
            <CardContent className="p-0">
              {order.items?.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Package className="w-12 h-12 mx-auto mb-2" />
                  <p>No items in this order</p>
                </div>
              ) : (
                <table className="table-industrial">
                  <thead>
                    <tr>
                      <th>Asset ID</th>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Rate/Week</th>
                      <th>Days/Weeks</th>
                      <th>Total</th>
                      <th>Delivered</th>
                      <th>Collected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items?.map((item, index) => (
                      <tr key={index}>
                        <td className="font-mono text-sm">{item.asset_id}</td>
                        <td className="font-body font-semibold">{item.name}</td>
                        <td><StatusBadge status={item.status} /></td>
                        <td className="font-mono text-sm">
                          {item.weekly_rate > 0 ? `£${item.weekly_rate?.toFixed(2)}` : '-'}
                        </td>
                        <td className="font-mono text-sm">
                          {item.actual_days ? (
                            <span>{item.actual_days}d / {item.hire_weeks}w</span>
                          ) : '-'}
                        </td>
                        <td className="font-heading font-bold text-green-600">
                          {item.calculated_total > 0 ? `£${item.calculated_total?.toFixed(2)}` : '-'}
                        </td>
                        <td>
                          {item.delivered ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td>
                          {item.collected ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes">
          <Card className="border-2 border-slate-200 shadow-industrial">
            <CardContent className="p-6">
              {order.notes ? (
                <div>
                  <p className="font-mono text-xs uppercase text-slate-400 mb-2">Notes</p>
                  <p className="font-body text-slate-700">{order.notes}</p>
                </div>
              ) : (
                <p className="text-slate-400 text-center py-8">No notes for this order</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Email Dialog */}
      <Dialog open={emailDialog.open} onOpenChange={(open) => setEmailDialog({ ...emailDialog, open })}>
        <DialogContent className="max-w-md border-2 border-slate-200 shadow-industrial">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold uppercase flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Email {emailDialog.type === 'delivery' ? 'Delivery' : 'Collection'} Note
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-heading font-bold uppercase text-xs">Recipient Email *</Label>
              <Input
                type="email"
                value={emailForm.recipient_email}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_email: e.target.value })}
                placeholder="client@example.com"
                className="mt-1 border-2 border-slate-200"
                data-testid="email-recipient-input"
              />
            </div>
            <div>
              <Label className="font-heading font-bold uppercase text-xs">Recipient Name</Label>
              <Input
                value={emailForm.recipient_name}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_name: e.target.value })}
                placeholder={order?.client_name || 'Client Name'}
                className="mt-1 border-2 border-slate-200"
              />
            </div>
            <div>
              <Label className="font-heading font-bold uppercase text-xs">Custom Message (Optional)</Label>
              <Textarea
                value={emailForm.custom_message}
                onChange={(e) => setEmailForm({ ...emailForm, custom_message: e.target.value })}
                placeholder="Add a personal note to the email..."
                className="mt-1 border-2 border-slate-200"
                rows={3}
              />
            </div>
            <div className="bg-slate-50 p-3 rounded text-sm">
              <p className="font-mono text-xs text-slate-500 uppercase mb-1">Will send:</p>
              <p className="text-slate-700">
                {emailDialog.type === 'delivery' ? 'Delivery' : 'Collection'} Note PDF for order <strong>{order?.order_number}</strong> to the recipient.
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setEmailDialog({ open: false, type: 'delivery' })}
                className="flex-1 border-2 border-slate-200"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailForm.recipient_email}
                className="flex-1 bg-primary hover:bg-primary-hover"
                data-testid="send-email-btn"
              >
                {sendingEmail ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
